import { AddBoxOutlined, NavigateBefore, NavigateNext } from '@mui/icons-material'
import { Button, Card, CardContent, Dialog, DialogContent, Grid, Icon, Typography } from '@mui/material'
import { NEW, OTHER, REFRESH } from 'common/components/action/ActionConstant'
import useActions from 'common/hooks/useActions'
import useFetch from 'common/hooks/useFetch'
import useTitles from 'common/hooks/useTitles'
import ApplicationConf from 'conf/ApplicationConf'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { BasicDialogAction, BasicDialogTitle } from 'common/components/modal/DialogComponents'
import Select from 'common/components/form/Select'
import Input from 'common/components/form/Input'
import { PRODUCT_LIST } from 'common/constants/Constants'
import DateTimePicker from 'common/components/form/DateTimePicker'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { createSubject, deleteSubject, updateSubject } from './RoadmapSlice'
import { isNil, orderBy } from 'lodash'
import { Draggable } from 'common/components/dragndrop/Draggable'
import { DndContext } from '@dnd-kit/core'
import { Droppable } from 'common/components/dragndrop/Droppable'
import ContextMenu from 'common/components/ContextMenu'
import useLocalStorage from 'common/hooks/useLocalStorage'
import { CardTitle } from 'common/components/card/Card'
import Chip from 'common/components/Chip'
import { INSTANCE_TYPE } from 'supervision/instance/constants/InstanceConstants'
import { normalizeString } from 'common/utils/StringUtil'
import { fetchUsers, selectUsers } from 'admin/users/UsersSlice'
import { STATUS } from 'common/constants/storeConstants'
import { Fieldset } from 'common/components/form/Fieldset'
import { getEndMonthFromTrimester, getStartMonthFromTrimester } from 'common/utils/DateUtil'
import { PASTEL_COLOR } from 'common/utils/ColorUtil'

const STATUS_SUBJECTS = [
    { value: 1, label: '1/5 Concept', icon: 'lightbulb', color: PASTEL_COLOR.grey },
    { value: 2, label: '2/5 Specs', icon: 'description', color: PASTEL_COLOR.blue },
    { value: 3, label: '3/5 Tickets', icon: 'confirmation_number', color: PASTEL_COLOR.blue },
    { value: 4, label: '4/5 En cours', icon: 'pending', color: PASTEL_COLOR.orange },
    { value: 5, label: '5/5 Terminé', icon: 'check_circle', color: PASTEL_COLOR.green },
]
const STATUS_DONE = 5

// Icône colorée d'un jalon, réutilisée dans la liste déroulante et sur la carte du sujet.
const getMilestoneIcon = (milestone, sx = {}) => (
    <Icon sx={{ color: milestone.color, ...sx }}>{milestone.icon}</Icon>
)

const DialogSubject = ({
    subject,
    onValidate,
    onClose,
}) => {
    const users = useSelector(selectUsers, shallowEqual)

    const [subjectData, setSubjectData] = useState(subject || {
        label: undefined,
        description: '',
        year: moment().year(),
        trimester: undefined,
        date: undefined,
    })

    const userList = useMemo(() => {
        return users.filter(u => u.email.includes('aquasys')).map(u => ({
            id: u.login,
            label: u.email.split('@')[0].replace('.', ' '),
            name: u.email.split('@')[0].replace('.', ' '),
        }))
    }, [users])

    return (
        <Dialog open={true} onClose={onClose} maxWidth='md' fullWidth>
            <BasicDialogTitle onClose={onClose}>
                {subject?.id ? 'Modifier le sujet' : 'Créer un nouveau sujet'}
            </BasicDialogTitle>
            <DialogContent>
                <Grid container spacing={1}>
                    <Grid size={9} container>
                        <Grid size={9}>
                            <Input
                                label='Sujet'
                                value={subjectData.label}
                                onChange={(v) => setSubjectData({ ...subjectData, label: v })}
                                required
                            />
                        </Grid>
                        <Grid size={3}>
                            <Input
                                label='Année'
                                type='number'
                                value={subjectData.year}
                                onChange={(v) => setSubjectData({ ...subjectData, year: Number(v) })}
                                required
                            />
                        </Grid>
                        <Grid size={12}>
                            <Input
                                label='Description'
                                value={subjectData.description}
                                onChange={(v) => setSubjectData({ ...subjectData, description: v })}
                                multiline
                                rows={10}
                            />
                        </Grid>
                    </Grid>
                    <Grid size={3} container direction='column'>
                        <Select
                            label='Trimestre'
                            items={[
                                { value: 1, label: '1' },
                                { value: 2, label: '2' },
                                { value: 3, label: '3' },
                                { value: 4, label: '4' },
                            ]}
                            value={subjectData.trimester}
                            onChange={(v) => setSubjectData({ ...subjectData, trimester: v })}
                            required
                        />
                        <DateTimePicker
                            label='Date'
                            value={subjectData.date}
                            onChange={(v) => setSubjectData({ ...subjectData, date: v })}
                        />
                        <Select
                            label='Produit'
                            items={PRODUCT_LIST}
                            value={subjectData.product}
                            onChange={(v) => setSubjectData({ ...subjectData, product: v })}
                        />
                        <Select
                            label='Référent'
                            items={userList}
                            value={subjectData.referent}
                            onChange={(v) => setSubjectData({ ...subjectData, referent: v })}
                        />
                        <Select
                            label='Status'
                            items={STATUS_SUBJECTS.map(s => ({ ...s, icon: getMilestoneIcon(s, { marginRight: '5px' }) }))}
                            value={subjectData.status}
                            onChange={(v) => setSubjectData({ ...subjectData, status: v })}
                            sortKey='value'
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <BasicDialogAction
                onValidate={() => onValidate(subjectData)}
                isDisabled={!subjectData.label || !subjectData.year || !subjectData.trimester}
            />
        </Dialog>
    )
}

DialogSubject.propTypes = {
    subject: PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
        description: PropTypes.string,
        year: PropTypes.number,
        trimester: PropTypes.number,
        date: PropTypes.number,
    }),
    onValidate: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
}

const TrimCard = ({
    trimester,
    subjects = [],
    activeSubject,
    onEditSubject = () => {},
    onDeleteSubject = () => {},
}) => (
    <Card>
        <CardTitle
            title={
                <Grid container justifyContent='space-between' alignItems='center'>
                    <Grid sx={{ fontSize: '0.9rem', fontStyle: 'italic' }}>
                        {getStartMonthFromTrimester(trimester)}
                    </Grid>
                    <Grid size='grow' sx={{ fontSize: '1.3rem', fontWeight: 'bold', textAlign: 'center' }} >
                        {`Trimestre ${trimester}`}
                    </Grid>
                    <Grid sx={{ fontSize: '0.9rem', fontStyle: 'italic', textAlign: 'end' }}>
                        {getEndMonthFromTrimester(trimester)}
                    </Grid>
                </Grid>
            }
            sx={{ paddingLeft: '10px' }}
        />
        <CardContent>
            {activeSubject && activeSubject?.trimester !== trimester && (
                <Droppable id={trimester}>
                    <Grid
                        container
                        spacing={1}
                        alignItems='center'
                        justifyContent='center'
                        marginTop={1}
                        padding={2}
                        sx={{
                            color: 'grey',
                            border: '1px solid grey',
                            borderRadius: '5px',
                        }}
                    >
                        <AddBoxOutlined fontSize='large' sx={{ color: 'grey' }} />
                        Ajouter au trimestre
                    </Grid>
                </Droppable>
            )}
            {orderBy(subjects, ['product'], ['asc']).map(subject => {
                const milestone = STATUS_SUBJECTS.find(s => s.value === subject.status)
                return (
                    <ContextMenu
                        key={subject.id}
                        actions={[{
                            label: 'Modifier le sujet',
                            onClick: () => onEditSubject(subject),
                            icon: 'edit',
                        }, {
                            label: 'Supprimer le sujet',
                            onClick: () => onDeleteSubject(subject.id),
                            icon: 'delete',
                        }]}
                    >
                        <Draggable id={subject.id}>
                            <Card variant='outlined' style={{ marginTop: 10 }}>
                                <CardContent>
                                    <Grid container spacing={1} alignItems='flex-start' justifyContent='flex-end'>
                                        <Grid size={12} container justifyContent='space-between'>
                                            <Typography variant='h6' component='div'>
                                                {subject.label}
                                            </Typography>
                                            <Chip
                                                label={subject.product}
                                                color={!isNil(subject.product) ? INSTANCE_TYPE.find(i => normalizeString(i.value) === normalizeString(subject.product))?.color : '#aaa'}
                                                sx={{ fontSize: '12px' }}
                                            />
                                        </Grid>
                                        {subject.description && (
                                            <Grid size={12}>
                                                <Typography color='text.secondary' sx={{ whiteSpace: 'pre-wrap' }}>
                                                    {subject.description?.length > 100 ? `${subject.description.substring(0, 100)}...` : subject.description}
                                                </Typography>
                                            </Grid>
                                        )}
                                        {milestone && (
                                            <Grid size='auto'>
                                                <Fieldset>
                                                    <Grid container alignItems='center' wrap='nowrap' spacing={0.5}>
                                                        {getMilestoneIcon(milestone, { fontSize: '16px' })}
                                                        <Typography color='text.secondary' sx={{ fontStyle: 'italic', fontSize: '10px' }}>
                                                            {milestone.label}
                                                        </Typography>
                                                    </Grid>
                                                </Fieldset>
                                            </Grid>
                                        )}
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Draggable>
                    </ContextMenu>
                )
            })}
        </CardContent>
    </Card>
)

TrimCard.propTypes = {
    trimester: PropTypes.number.isRequired,
    subjects: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
        description: PropTypes.string,
        year: PropTypes.number,
        trimester: PropTypes.number,
        date: PropTypes.number,
        product: PropTypes.string,
    })),
    activeSubject: PropTypes.object,
    onEditSubject: PropTypes.func,
    onDeleteSubject: PropTypes.func,
}

const RoadmapApp = () => {
    const [filter, setFilter] = useLocalStorage('filterRoadMap', { year: moment().year() })

    const [openSubjectDialog, setOpenSubjectDialog] = useState(false)
    const [subjectToEdit, setSubjectToEdit] = useState({})


    const {
        data: subjects,
        status: loading,
        reload: reloadSubjects,
    } = useFetch(ApplicationConf.gdl.subjects())

    const [activeElement, setActiveElement] = useState()
    const activeSubject = useMemo(() => subjects?.find(s => s.id === activeElement?.id), [activeElement, subjects])

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(fetchUsers())
    }, [dispatch])

    useTitles(() => [
        { label: 'Roadmap', href: 'roadmap' },
    ], [])

    useActions(() => [{
        type: NEW,
        label: 'Ajouter un sujet',
        onClick: () => {
            setSubjectToEdit({ year: filter?.year })
            setOpenSubjectDialog(true)
        },
    }, {
        type: OTHER,
        icon: filter?.hideDone ? 'visibility' : 'visibility_off',
        tooltip: filter?.hideDone ? 'Afficher les sujets terminés' : 'Masquer les sujets terminés',
        onClick: () => setFilter(prev => ({ ...prev, hideDone: !prev?.hideDone })),
    }, {
        type: REFRESH,
        label: 'Rafraîchir',
        onClick: () => reloadSubjects(),
        loading: loading === STATUS.LOADING,
    }], [filter, loading])

    const yearSubjects = subjects?.filter(subject => subject.year === filter?.year && !(filter?.hideDone && subject.status === STATUS_DONE)) || []

    const onEditSubject = (subject) => {
        setSubjectToEdit(subject)
        setOpenSubjectDialog(true)
    }

    const onDeleteSubject = (id) => dispatch(deleteSubject(id)).then(() => reloadSubjects())

    return (
        <Grid container spacing={2} alignItems='flex-start'>
            <Grid size={12}>
                <Card>
                    <CardContent>
                        <Grid container spacing={2} alignItems='center' justifyContent='space-between'>
                            <Button
                                variant='outlined'
                                onClick={() => setFilter({ ...filter, year: filter?.year - 1 })}
                                startIcon={<NavigateBefore />}
                            >
                                Année précédente
                            </Button>
                            <h2>{`Roadmap ${filter?.year}`}</h2>
                            <Button
                                variant='outlined'
                                onClick={() => setFilter({ ...filter, year: filter?.year + 1 })}
                                endIcon={<NavigateNext />}
                            >
                                Année suivante
                            </Button>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>
            <DndContext
                onDragEnd={({ active, over }) => {
                    if (over?.id) {
                        dispatch(updateSubject({
                            ...subjects.find(({ id }) => id === active.id),
                            trimester: over.id,
                        })).then(() => reloadSubjects())
                    }
                    setActiveElement()
                }}
                onDragStart={({ active }) => setActiveElement(active)}
                onDragCancel={() => {
                    setActiveElement()
                }}
            >
                <Grid size={3}>
                    <TrimCard
                        trimester={1}
                        subjects={yearSubjects.filter(subject => subject.trimester === 1)}
                        activeSubject={activeSubject}
                        onEditSubject={onEditSubject}
                        onDeleteSubject={onDeleteSubject}
                    />
                </Grid>
                <Grid size={3}>
                    <TrimCard
                        trimester={2}
                        subjects={yearSubjects.filter(subject => subject.trimester === 2)}
                        activeSubject={activeSubject}
                        onEditSubject={onEditSubject}
                        onDeleteSubject={onDeleteSubject}
                    />
                </Grid>
                <Grid size={3}>
                    <TrimCard
                        trimester={3}
                        subjects={yearSubjects.filter(subject => subject.trimester === 3)}
                        activeSubject={activeSubject}
                        onEditSubject={onEditSubject}
                        onDeleteSubject={onDeleteSubject}
                    />
                </Grid>
                <Grid size={3}>
                    <TrimCard
                        trimester={4}
                        subjects={yearSubjects.filter(subject => subject.trimester === 4)}
                        activeSubject={activeSubject}
                        onEditSubject={onEditSubject}
                        onDeleteSubject={onDeleteSubject}
                    />
                </Grid>
            </DndContext>
            {openSubjectDialog && <DialogSubject
                subject={subjectToEdit}
                onClose={() => setOpenSubjectDialog(false)}
                onValidate={(subject) => {
                    setOpenSubjectDialog(false)
                    dispatch(subject?.id ? updateSubject(subject) : createSubject(subject)).then(() => reloadSubjects())
                }}
            />}
        </Grid>
    )
}

export default RoadmapApp